/* QUICK VIEW – Product video UX (v9: robust + clean-up)
   What it does (Quick View only):
   - Autoplay (muted, inline) the currently visible video when the modal opens
   - When switching away & back, restart from 0 and play
   - Hide native controls (and remove if the theme re-adds them)
   - Works with translate-based/carousel sliders via area-based active slide detection
   - Cleans up observers/timers when the modal closes
*/
;(() => {
  const MODAL_SEL = [
    '.quick-product-modal',
    '.quick-view-modal',
    '.modal--quick-view',
    '.product-quickview',
    '[data-quick-view-modal]',
    '[role="dialog"][aria-modal="true"]'
  ].join(', ');

  const SLIDE_SEL   = '.product__media-item[data-media-item-id]';
  const VIDEO_Q     = 'video';
  const VIEWPORT_CANDIDATES = [
    '.quick-product__left .scroll-slider__slider',
    '.scroll-slider__slider',
    '.scroll-slider__viewport',
    '.product__media'
  ];

  const LOG_PREFIX = '[QV v9]';
  const dbg = (...a) => { if (window.QV_VIDEO_DEBUG) console.log(LOG_PREFIX, ...a); };

  // --- Video helpers -------------------------------------------------------
  function prepVideo(v){
    if (!v) return;
    v.muted = true;
    v.playsInline = true; v.setAttribute('playsinline','');
    v.autoplay = true;
    v.controls = false; v.removeAttribute('controls');
    v.setAttribute('controlsList','nodownload noplaybackrate nofullscreen');
    v.setAttribute('disablepictureinpicture','');
  }
  function hideControlsWatcher(v){
    if (!v || v.__qvHideMO) return;
    const mo = new MutationObserver(() => {
      if (v.controls) { v.controls = false; v.removeAttribute('controls'); }
    });
    mo.observe(v, { attributes: true, attributeFilter: ['controls'] });
    v.__qvHideMO = mo;
  }
  function safePause(v){ try { v.pause(); } catch(_) {} }
  function playFromStart(v){
    if (!v) return;
    prepVideo(v); hideControlsWatcher(v);
    try { v.currentTime = 0; } catch(_) {}
    let tries = 0;
    const attempt = () => {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => { if (tries++ < 8) setTimeout(() => { prepVideo(v); attempt(); }, 140); });
      }
    };
    attempt();
  }

  // Visible area of `el` inside `root` (to handle translate-based sliders)
  function visibleArea(el, root){
    const r = el.getBoundingClientRect();
    const rr = (root || el.parentElement || el).getBoundingClientRect();
    const x = Math.max(r.left, rr.left);
    const y = Math.max(r.top, rr.top);
    const w = Math.max(0, Math.min(r.right, rr.right) - x);
    const h = Math.max(0, Math.min(r.bottom, rr.bottom) - y);
    return w * h;
  }

  function findViewport(modal){
    for (const sel of VIEWPORT_CANDIDATES) {
      const vp = modal.querySelector(sel);
      if (vp) return vp;
    }
    return modal;
  }

  // --- Per-modal controller -----------------------------------------------
  function bindModal(modal){
    if (!modal || modal.__qvVideoBound) return;
    modal.__qvVideoBound = true;
    dbg('init modal');

    const vp = findViewport(modal);

    // state
    let slides = [];
    let activeSlide = null;
    let activeId = null;
    let pollId = null;
    const observers = [];

    const refreshSlides = () => { slides = Array.from(modal.querySelectorAll(SLIDE_SEL)); };

    const bestSlide = () => {
      refreshSlides();
      if (!slides.length) return null;
      let best = slides[0], bestA = -1;
      for (const s of slides) {
        const a = visibleArea(s, vp);
        if (a > bestA) { bestA = a; best = s; }
      }
      return best;
    };

    const videoIn = (slide) => slide ? slide.querySelector(VIDEO_Q) : null;

    function handleSwitch(){
      const next = bestSlide();
      if (!next) return;

      const nextId = next.getAttribute('data-media-item-id');
      if (nextId !== activeId) {
        // switched to a different slide
        const prevV = videoIn(activeSlide);
        if (prevV) safePause(prevV);

        activeSlide = next;
        activeId = nextId;

        const v = videoIn(activeSlide);
        if (v) playFromStart(v);
        return;
      }

      // Same slide regained visibility (e.g., after translate)
      const vSame = videoIn(activeSlide);
      if (vSame && vSame.paused) playFromStart(vSame);
    }

    // Kickstart: if the first visible slide is video, start it
    (function initStart(){
      activeSlide = bestSlide();
      activeId = activeSlide?.getAttribute('data-media-item-id') || null;
      const v = videoIn(activeSlide);
      if (v) playFromStart(v);
    })();

    // Observe the gallery for changes (DOM/class/style)
    const galleryMO = new MutationObserver(() => setTimeout(handleSwitch, 40));
    galleryMO.observe(vp, { subtree: true, childList: true, attributes: true, attributeFilter: ['class','style','hidden','aria-hidden'] });
    observers.push(galleryMO);

    // Also react to thumbnail interactions explicitly
    const thumbsRoot = modal.querySelector('.thumbnail-list, .product-thumbnails, [data-thumbnails]') || modal;
    const thumbHandler = () => setTimeout(handleSwitch, 60);
    thumbsRoot.addEventListener('click', thumbHandler, true);
    thumbsRoot.addEventListener('mouseenter', thumbHandler, true);

    // Short polling for the first few seconds to cover animation frames
    let ticks = 0;
    pollId = setInterval(() => {
      handleSwitch();
      if (++ticks > 40) { clearInterval(pollId); pollId = null; } // ~8s
    }, 200);

    // Clean-up when modal closes / is removed
    const cleanup = () => {
      if (pollId) { clearInterval(pollId); pollId = null; }
      observers.forEach(o => { try { o.disconnect(); } catch(_) {} });
      observers.length = 0;
      const vids = modal.querySelectorAll(VIDEO_Q);
      vids.forEach(v => { if (v.__qvHideMO){ try { v.__qvHideMO.disconnect(); } catch(_) {} delete v.__qvHideMO; } });
      modal.__qvVideoBound = false;
    };

    // Heuristics: close events
    modal.addEventListener('close', cleanup, { once: true });
    modal.addEventListener('transitionend', (e) => {
      // If the modal was hidden via CSS class, re-check visibility
      if (!modal.isConnected || (getComputedStyle(modal).display === 'none' || getComputedStyle(modal).visibility === 'hidden')) {
        cleanup();
      }
    });
    // If the node is removed from DOM:
    const removeMO = new MutationObserver(() => {
      if (!modal.isConnected) { cleanup(); removeMO.disconnect(); }
    });
    removeMO.observe(document.documentElement, { childList: true, subtree: true });
  }

  // --- Bootstrapping: bind on open/DOM changes -----------------------------
  function boot(){
    document.querySelectorAll(MODAL_SEL).forEach((m) => {
      // visible-enough heuristic
      const style = getComputedStyle(m);
      const isShown = style.display !== 'none' && style.visibility !== 'hidden' && (m.offsetWidth + m.offsetHeight) > 0;
      if (isShown) bindModal(m);
    });
  }

  // Open triggers (broad)
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-quick-view], .quick-view, .quick-view-button, [data-open-quick-view]')) {
      setTimeout(boot, 0);
      setTimeout(boot, 80);
      setTimeout(boot, 300);
      setTimeout(boot, 800);
    }
  });

  // Global observers (catch AJAX-injected modals)
  const gmo = new MutationObserver(() => boot());
  gmo.observe(document.documentElement, { subtree: true, childList: true, attributes: true });

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
